import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosRoleContractService } from '@/services/RorosService';
import { rorosKeys } from './keys';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosRoleContracts(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.roleContracts.all, params] as const,
    queryFn: () => RorosRoleContractService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosRoleContractTemplates() {
  return useQuery({
    queryKey: rorosKeys.roleContracts.templates,
    queryFn: () => RorosRoleContractService.listTemplates(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosRoleContract(id: string) {
  return useQuery({
    queryKey: rorosKeys.roleContracts.detail(id),
    queryFn: () => RorosRoleContractService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosRoleContractCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosRoleContractService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.all });
    },
  });
}

export function useRorosRoleContractClone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, payload }: { templateId: string; payload: Record<string, unknown> }) =>
      RorosRoleContractService.cloneFromTemplate(templateId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.all });
    },
  });
}

export function useRorosRoleContractUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosRoleContractService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.all });
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.detail(id) });
    },
  });
}

export function useRorosRoleContractActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosRoleContractService.activate(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.all });
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.detail(id) });
    },
  });
}

export function useRorosRoleContractArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosRoleContractService.archive(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.all });
      qc.invalidateQueries({ queryKey: rorosKeys.roleContracts.detail(id) });
    },
  });
}
